import {
    IFlowGalleryRepositories,
    IFlowGalleryTemplate,
    IImportedFlowTemplate,
    IImportedTemplateResp,
    TemplateType,
    TRepoKey
} from '~/components/FlowGallery/types/flow-gallery.types';
import {
    TFetchCompanyFlows,
    TFlowActionType,
    TImportPDFFileType
} from '~/components/CreateFlow/types/create-flow.types';
import {
    IEnrichedFlowTemplate,
    IFlowTemplate,
    IRecentlySent,
    TBlockType
} from '~/types/model.types';
import { IFlowResponse } from '~/types/server.types';
import { UserTemplate } from '~/components/GalleryDashboard/gallery-dashboard.types';
import { TCreateTemplateType } from '~/components/Templates/types/templates.types';
import { BaseHttpAdapter } from '~/services/adapters/base-http.adapter';

export interface IFetchMostRankedTemplatesOptions {
    filters?: TFlowActionType[];
    excludeFlowIds?: string[];
    pageNumber?: number;
}

export interface IFetchMostUsedTemplatesOptions {
    activeFilters?: TFlowActionType[];
    excludeFlowIds?: string[];
    pageNumber?: number;
    limit?: number;
    enrich?: boolean;
}
export class TemplatesAdapter extends BaseHttpAdapter {
    async _fetchTemplates(params: {
        filters: {
            type: TemplateType[];
            tag: string[];
            repository: TRepoKey[];
        };
    }) {
        return this.apiService.apiRequest<IFlowGalleryTemplate[]>({
            method: 'get',
            url: '/api/v2/templates_repository_records/find',
            params
        });
    }

    async fetchTemplatesRepository(repositoryKey: TRepoKey) {
        return this.apiService.apiRequest<IFlowGalleryRepositories>({
            method: 'get',
            url: `/api/v2/templates_repository/${repositoryKey}`
        });
    }

    async saveTemplate(templateId: string) {
        return this.apiService.apiRequest<IImportedTemplateResp>({
            method: 'put',
            url: '/api/v2/templates_repository_record/import',
            data: {
                filters: { id: [templateId] }
            }
        });
    }

    async downloadTemplate(publishedTemplateId: string, title?: string) {
        return this.apiService.apiRequest<IImportedFlowTemplate>({
            method: 'put',
            url: `/api/v2/flow_gallery/flow/${publishedTemplateId}/download`,
            data: {
                title
            }
        });
    }

    async fetchRecentlySentFlows(
        limit?: number,
        filters: TFlowActionType[] = []
    ) {
        return this.apiService.apiRequest<IRecentlySent>({
            method: 'get',
            url: '/api/v2/search/flows/recently_sent_files_and_flows',
            params: {
                limit,
                filters
            }
        });
    }

    async fetchMostUsedTemplates({
        limit,
        activeFilters = [],
        excludeFlowIds,
        enrich = false
    }: IFetchMostUsedTemplatesOptions = {}) {
        return this.apiService.apiRequest<IFlowTemplate[]>({
            method: 'get',
            url: '/api/v2/search/flows/most_used',
            params: {
                limit,
                filters: activeFilters,
                exclude_flow_ids: excludeFlowIds,
                enrich
            }
        });
    }

    async fetchMostRankedTemplates({
        filters = [],
        excludeFlowIds,
        pageNumber
    }: IFetchMostRankedTemplatesOptions = {}) {
        return this.apiService.apiRequest<IEnrichedFlowTemplate[]>({
            method: 'get',
            url: '/api/v2/flows/template/most_ranked',
            params: {
                filters,
                exclude_flow_ids: excludeFlowIds,
                page_number: pageNumber
            }
        });
    }

    async createFlowInstance({
        templateId,
        workspaceId,
        flowSource
    }: {
        templateId: string;
        workspaceId: string;
        flowSource?: string;
    }) {
        return this.apiService.apiRequest<any>({
            method: 'post',
            url: '/api/v2/flow/instance',
            params: {
                workspace_id: workspaceId,
                template_id: templateId,
                flow_source: flowSource || undefined
            }
        });
    }

    async createFlowInstanceWithProject({
        templateId,
        projectData,
        flowSource
    }: {
        templateId: string;
        projectData: any;
        flowSource?: string;
    }) {
        return this.apiService.apiRequest<any>({
            method: 'post',
            url: '/api/v2/flow/instance',
            data: {
                template_id: templateId,
                event: projectData,
                flow_source: flowSource
            }
        });
    }

    createFlowInstanceFromServices(templateId: string, flowId: string) {
        return this.apiService.apiRequest<any>({
            method: 'post',
            url: '/api/v2/flow/instance_from_service_selection',
            params: {
                flow_id: flowId,
                template_id: templateId
            }
        });
    }

    async createFlowFromLegacyFile(fileId: string, workspaceId: string) {
        return this.apiService.apiRequest<any>({
            method: 'post',
            url: '/api/v2/flow/instance_from_legacy',
            params: {
                template_id: fileId,
                workspace_id: workspaceId
            }
        });
    }

    fetchCompanyFlows({
        companyId,
        filters,
        folder_id,
        flow_type
    }: TFetchCompanyFlows) {
        return this.apiService.apiRequest<IFlowTemplate[]>({
            method: 'get',
            url: `/api/v2/search/company/${companyId}/flows`,
            params: {
                filters,
                flow_type,
                folder_id
            },
            networkLabel: `fetchCompanyFlows_${companyId}`
        });
    }

    async fetchLeadFormTemplates() {
        return this.apiService.apiRequest<IFlowGalleryTemplate[]>({
            method: 'get',
            url: `/api/v2/public/public_flows`
        });
    }

    async publishGalleryTemplate(
        template: IFlowGalleryTemplate,
        repositories: TRepoKey[]
    ) {
        return this.apiService.apiRequest<IFlowGalleryTemplate>({
            method: 'post',
            url: '/api/v2/templates_repository/template',
            data: {
                ...template,
                publisher_email: template.publisher?.email,
                type: TemplateType.Flow,
                repositories
            }
        });
    }

    async saveGalleryTemplate(
        template: IFlowGalleryTemplate,
        currentRepository: TRepoKey
    ) {
        return this.apiService.apiRequest<IFlowGalleryTemplate>({
            method: 'put',
            url: '/api/v2/templates_repository/template',
            data: {
                ...template,
                publisher_email: template.publisher?.email,
                type: TemplateType.Flow,
                repositories: [currentRepository]
            }
        });
    }

    async modifyCustomTemplate(flowId: string, blocks: TCreateTemplateType[]) {
        return this.apiService.apiRequest({
            method: 'put',
            url: `/api/v2/templates_repository/${flowId}/custom_template`,
            data: { blocks }
        });
    }

    async deleteGalleryTemplate(template_id: string, repository: TRepoKey) {
        return this.apiService.apiRequest<IFlowGalleryTemplate>({
            method: 'delete',
            url: '/api/v2/templates_repository/template',
            data: {
                template_id,
                type: TemplateType.Flow,
                repository
            }
        });
    }

    async updateTags(repositoryKey: TRepoKey, tags: string[]) {
        return this.apiService.apiRequest<IFlowGalleryTemplate>({
            method: 'put',
            url: `/api/v2/templates_repository/${repositoryKey}`,
            data: { tags }
        });
    }

    async fetchSendTestTemplates(userId: string) {
        return this.apiService.apiRequest<any[]>({
            method: 'get',
            url: `/api/v2/users/${userId}/onboarding_sequence/send_test_templates`
        });
    }

    async getUserTemplates(user_email: string) {
        return this.apiService.apiRequest<UserTemplate[]>({
            method: 'get',
            url: `/api/v2/flow_gallery/user_flow_templates`,
            params: { user_email }
        });
    }

    shareTemplate(templateId: string, email: string) {
        return this.apiService.apiRequest({
            method: 'post',
            url: `/api/v2/flow/${templateId}/share_flow_template`,
            params: { receivers: [email] }
        });
    }

    previewSharedTemplate(metadataId) {
        return this.apiService.apiRequest<IFlowResponse>({
            method: 'get',
            url: `/api/v2/flows/shared_templates/preview`,
            params: {
                metadata_id: metadataId
            }
        });
    }

    saveSharedTemplate(metadataId, folderId) {
        return this.apiService.apiRequest<IFlowResponse>({
            method: 'post',
            url: `/api/v2/flows/shared_templates/save`,
            params: {
                metadata_id: metadataId,
                folder_id: folderId
            }
        });
    }

    importJBTemplates() {
        return this.apiService.apiRequest<any>({
            method: 'put',
            url: '/api/v2/templates_repository/influencer/john_branch'
        });
    }

    importFUTemplate() {
        return this.apiService.apiRequest<IFlowTemplate>({
            method: 'put',
            url: '/api/v2/templates_repository/freelancers_union/import'
        });
    }

    importPublicTemplate({
        templateId,
        isGalleryTemplate
    }: {
        templateId: string;
        isGalleryTemplate: boolean;
    }) {
        return this.apiService.apiRequest<IFlowTemplate>({
            method: 'post',
            url: `/api/v2/flows/public_template/${templateId}/import`,
            data: {
                template_type: isGalleryTemplate
                    ? 'public_member_sharing'
                    : 'template_gallery'
            }
        });
    }

    importPricedTemplate({ redeemCode }: { redeemCode: string }) {
        return this.apiService.apiRequest<IFlowTemplate>({
            method: 'post',
            url: `/api/v2/flows/public_template/${redeemCode}/redeem`
        });
    }

    publishTemplate(templateId: string, isPublic: boolean) {
        return this.apiService.apiRequest<IFlowTemplate>({
            method: 'put',
            url: `/api/v2/flow/${templateId}/template/publish`,
            data: {
                public: isPublic
            }
        });
    }

    createTemplateFromPDF(data: {
        pdf_s3_url: string;
        pdf_file_name: string;
        flow_types: TImportPDFFileType[];
        extra_blocks: TBlockType[];
    }) {
        return this.apiService.apiRequest<void>({
            method: 'post',
            url: `/api/v2/flow_from_pdf/template`,
            data
        });
    }

    changePublicTemplateConfiguration({
        templateId,
        configType,
        state
    }: {
        templateId: string;
        configType: string;
        state: boolean;
    }) {
        return this.apiService.apiRequest<IFlowTemplate>({
            method: 'put',
            url: `/api/v2/flow/${templateId}/template/settings`,
            data: {
                config_type: configType,
                state: state
            }
        });
    }

    updatePublicConfigurationSelledByTemplate({
        templateId,
        leadFormId
    }: {
        templateId: string;
        leadFormId: string;
    }) {
        return this.apiService.apiRequest<IFlowTemplate>({
            method: 'put',
            url: `/api/v2/flow/${templateId}/template/selled_lead_form`,
            data: {
                lead_form_id: leadFormId
            }
        });
    }
}
